import {
  type KycWalk,
  kycFormPosition,
  walkOrder,
  walkRound,
} from './kycFormPosition'
import { FiatKycPosition } from './FiatKycPosition'
import type {
  FiatProvider,
  FormDescriptor,
  FormField,
  FormValues,
  JsonValue,
  Quote,
} from '@funkit/connect-core'
import { useForm, useSelector } from '@tanstack/react-form'
import type { TFunction } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Animated,
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextInput,
  View,
} from 'react-native'
import Reanimated, { useSharedValue, withTiming } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useKeyboardFollow } from '../flow/useKeyboardHeight'
import { useHapticFeedback } from '../haptics/HapticsContext'
import { useFlowStepFrame } from '../hooks/useNestedSheetHeight'
import { KycGlassBackdrop } from './KycGlassBackdrop'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import type { FieldReturnKey } from './FiatFormFieldRow'
import { FiatKycFieldControl } from './FiatKycFieldControl'
import { kycFieldValidators } from './kycFieldValidators'
import { FiatStatusScreen } from './FiatStatusScreen'
import {
  EXPECTED_AMOUNT_PER_MONTH,
  GROSS_ANNUAL_INCOME,
  type KycSectionPage,
  TRANSACTIONS_PER_MONTH,
  isDigitsField,
  isFixedWidthField,
  isFocusableField,
  isInlineChoiceSection,
  sectionPages,
  unsupportedFields,
} from './formLayout'
import { FiatBackButton } from './FiatBackButton'
import { ssnLength } from './ssnLength'
import { KycSecurityIcon } from '../icons/KycSecurityIcon'
import { DATE_PICKER_IS_INLINE } from './nativeDatePicker'
import { FiatDatePickerDock } from './FiatDatePickerDock'
import { FiatFormAdvance, advanceDockHeight } from './FiatFormAdvance'
import { formValidationError } from './formValidationError'
import { KYC_FORM_METRICS } from './kycFormTheme'
import {
  type KycFieldIssue,
  blankCountryFields,
  carriedBlankFields,
  firstIssue,
  stringValue,
  typedValues,
  validateKycForm,
} from './kycFormState'
import { settleAttempt } from './kycFormAttempt'
import { sectionDetail } from './sectionDetail'
import { useDatePickerDock } from './useDatePickerDock'
import { isDarkColor } from '../theme/isDarkColor'
import {
  MODAL_ENTERING,
  MODAL_EXITING,
  PAGE_SLIDE_DISTANCE,
  PAGE_TIMING,
  pageEntering,
} from '../utils/motion'

export interface FiatFormScreenProps {
  form: FormDescriptor
  /** Present when identity is collected after a provider has priced the round. */
  quote?: Quote
  /** The provider the round names, for the consent the intro takes. */
  provider?: FiatProvider
  values: FormValues
  /**
   * The buyer's region, ISO 3166-1 alpha-2. A blank COUNTRY field is filled
   * with it on arrival, so nobody is asked to type "US" into a box the frames
   * never drew.
   */
  defaultCountry?: string
  /**
   * What the buyer already typed in an earlier round of this session. A field
   * blank here and answered there is filled in for them; anything they have
   * touched on this round is theirs. Never persisted — it lives as long as the
   * screen does.
   */
  carriedValues?: FormValues
  /** The form's own error, when the server re-entered it carrying one. */
  errorMessage?: string
  /** The rounds walked so far, so the dots carry on from the earlier ones. */
  walk?: KycWalk
  isSubmitting: boolean
  onChangeField: (fieldId: string, value: JsonValue) => void
  /** Several fields at once, as one write — see `formScreenProps`. */
  onPrefill?: (values: FormValues) => void
  onSubmit: () => void
  onClose: () => void
}

/**
 * The identity form (KYC screen 3), stepped one section at a time as the
 * design draws it: name, then address, then date of birth.
 *
 * Only the last section submits — the ones before it advance locally, because
 * the contract sends the form whole and has no per-page call.
 */
export function FiatFormScreen(props: FiatFormScreenProps) {
  const { t } = useFunkitTranslation()
  // Navigation belongs to the same round as the fields and validation.
  const [step, setStep] = useState(0)
  const [roundId, setRoundId] = useState(props.form.formId)
  if (roundId !== props.form.formId) {
    setRoundId(props.form.formId)
    setStep(0)
  }

  const pages = sectionPages(props.form.fields)

  // Three frames, three pages. A form asking for anything they do not draw
  // cannot be completed here, and a form quietly missing a field the server
  // requires is worse than saying so.
  if (unsupportedFields(props.form.fields).length > 0 || pages.length === 0) {
    return (
      <FiatStatusScreen
        tone="failure"
        title={t('fiat.unsupported')}
        onClose={props.onClose}
      />
    )
  }

  // A form can shrink under a step the user already reached; the last page is
  // where they land. Clamped here, once, so the page drawn and the page judged
  // last are the same one.
  const shownStep = Math.min(step, pages.length - 1)

  // A second round is a different form: its values, touched marks and
  // validation start over rather than inheriting the first round's.
  //
  // The form opens and closes as transitions.dev's modal does — up from 0.96
  // and back down to it — since it stands over the amount screen rather than
  // being the next page of it.
  return (
    <Reanimated.View entering={MODAL_ENTERING} exiting={MODAL_EXITING}>
      <FiatFormContent
        key={props.form.formId}
        {...props}
        pages={pages}
        step={shownStep}
        onStep={setStep}
      />
    </Reanimated.View>
  )
}

interface FiatFormContentProps extends FiatFormScreenProps {
  pages: KycSectionPage[]
  step: number
  onStep: (step: number) => void
}

/** One round of the form: what is typed, what is refused, and which page is up. */
export function FiatFormContent({
  form,
  values,
  defaultCountry,
  carriedValues,
  errorMessage,
  walk,
  isSubmitting,
  onChangeField,
  onPrefill,
  onSubmit,
  onClose,
  pages,
  step,
  onStep,
}: FiatFormContentProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const haptic = useHapticFeedback()
  const { height, paddingTop } = useFlowStepFrame()
  const insets = useSafeAreaInsets()
  const keyboard = useKeyboardFollow(true)
  const keyboardHeight = keyboard.height
  // Keyed by field id rather than ordered: the step re-renders as it is typed
  // into, and a positional list would drift from the fields it names.
  const inputs = useRef<Record<string, TextInput | null>>({})

  const page = step < 0 ? undefined : pages[step]
  const isLastStep = step === pages.length - 1
  const pageFields = page?.rows.flat() ?? []
  // `DETAILS` is a questionnaire too, but it keeps the chooser: it holds
  // whatever the server asks for that no frame drew.
  const isInlineChoice =
    page !== undefined && isInlineChoiceSection(page.section)
  const isQuestionnaire = isInlineChoice || page?.section === 'DETAILS'
  const isAddress =
    page?.section === 'ADDRESS' ||
    page?.section === 'COUNTRY' ||
    page?.section === 'NATIONALITY'
  const isDatePage = page?.section === 'DOB'
  // The wheel is centred on the frame, so what stands above it has to be
  // measured: a title that wraps to one line or three in another language
  // would otherwise push it off centre. Seeded with the design's two lines
  // so the first paint is already right and the measure only corrects it.
  // What the docked control actually took: its notice wraps, so the metrics
  // are a floor rather than the figure.
  const [dockHeightDrawn, setDockHeightDrawn] = useState(0)
  const [headingHeight, setHeadingHeight] = useState(
    KYC_FORM_METRICS.questionnaireTitleLineHeight * 2,
  )

  // Which way the last turn went, for transitions.dev's page slide: the page
  // ahead waits 8px to the right and the page behind 8px to the left. Held
  // twice because the two readers run on different threads — the entering
  // keyframe is chosen while rendering, the exiting one on the UI thread as
  // the old page leaves.
  const turnedForward = useRef(true)
  const turnDirection = useSharedValue<1 | -1>(1)
  const turn = (next: number) => {
    const forward = next >= step
    turnedForward.current = forward
    turnDirection.value = forward ? 1 : -1
    onStep(next)
  }
  const exitPage = () => {
    'worklet'
    return {
      initialValues: { opacity: 1, transform: [{ translateX: 0 }] },
      animations: {
        opacity: withTiming(0, PAGE_TIMING),
        transform: [
          {
            translateX: withTiming(
              -PAGE_SLIDE_DISTANCE * turnDirection.value,
              PAGE_TIMING,
            ),
          },
        ],
      },
    }
  }

  // Read once: the form is the source while this round is up, and the flow's
  // copy is written through below, so a later prop is our own write returning.
  const [defaultValues] = useState(() => typedValues(values))
  const kyc = useForm({
    defaultValues,
    validators: {
      // On mount too, so a blank required field gates its page before anything
      // is typed; the row itself stays quiet until the page has been tried.
      onMount: ({ value }) => ({ fields: validateKycForm(form, value) }),
      onChange: ({ value }) => ({ fields: validateKycForm(form, value) }),
    },
    listeners: {
      // The flow reducer holds what the transition posts, so every edit is
      // written through as it happens.
      onChange: ({ fieldApi }) =>
        onChangeField(fieldApi.name, fieldApi.state.value),
    },
    // Every try goes through TanStack's submit, whichever page it is on: a
    // page before the last has fields still blank after it, so the form as a
    // whole is invalid and it is the invalid callback that decides. Only the
    // last page, with everything clean, reaches the valid one.
    onSubmit: () => settle(),
    onSubmitInvalid: () => settle(),
  })
  const fieldMeta = useSelector(kyc.store, (state) => state.fieldMeta)
  const country = useSelector(
    kyc.store,
    (state) =>
      stringValue(state.values.TAX_RESIDENCE_COUNTRY) ||
      stringValue(state.values.RESIDENTIAL_ADDRESS_COUNTRY) ||
      defaultCountry,
  )

  const taxField = pageFields.find((field) => field.id === 'TAX_IDENTIFIER')
  const taxLength =
    taxField === undefined ? undefined : ssnLength(taxField, country)
  const submissionAttempts = useSelector(
    kyc.store,
    (state) => state.submissionAttempts,
  )

  const issueOf = (fieldId: string) => firstIssue(fieldMeta[fieldId]?.errors)
  const pageIssues = pageFields
    .map((field) => issueOf(field.id))
    .filter((issue): issue is KycFieldIssue => issue !== undefined)
  const pageIsClean = pageIssues.length === 0

  /**
   * What a try does once TanStack has re-validated. Read off the store rather
   * than the render, since the validation it follows has not been rendered
   * yet; the decision itself is `settleAttempt`'s.
   */
  const settle = () => {
    const issues = validateKycForm(form, kyc.state.values)
    const meta = kyc.state.fieldMeta
    const outcome = settleAttempt({
      pages,
      step,
      isRefused: (field) =>
        issues[field.id] !== undefined ||
        firstIssue(meta[field.id]?.errors) !== undefined,
    })
    switch (outcome.kind) {
      case 'REFUSED':
        refusedPages.current.add(step)
        // The design's answer to a refused try: the fields tint (see
        // `attempted` below) and the phone says no.
        haptic('error')
        return
      case 'TURN':
        turn(outcome.to)
        return
      case 'JUMP':
        refusedPages.current.add(outcome.to)
        haptic('error')
        jumpedTo.current = outcome.to
        turn(outcome.to)
        return
      case 'SUBMIT':
        onSubmit()
    }
  }
  const attempt = () => {
    if (!isSubmitting) {
      void kyc.handleSubmit()
    }
  }
  useEffect(() => {
    if (isSubmitting) {
      Keyboard.dismiss()
    }
  }, [isSubmitting])

  // A page is "tried" once the user has asked to leave it and been refused.
  // TanStack counts tries for the whole form, so each page remembers the count
  // it arrived at; the difference is this page's own. A page jumped back to
  // was tried by the Confirm that sent the user there, so it starts one behind.
  const refusedPages = useRef(new Set<number>())
  const jumpedTo = useRef<number>(undefined)
  const attemptsAtEntry = useMemo(() => {
    const attempts = kyc.state.submissionAttempts
    return jumpedTo.current === step ? attempts - 1 : attempts
  }, [step])
  const attempted =
    submissionAttempts > attemptsAtEntry || refusedPages.current.has(step)

  useEffect(() => {
    // Consumed by the memo above; a later return to the page is a fresh one.
    jumpedTo.current = undefined
    // TanStack clears a field's errors as its `Field` unmounts, and turning a
    // page unmounts every field on it. Re-run the form's validator once the
    // new page is up so a refusal left behind still gates the last step.
    void kyc.validate('change')
  }, [step])
  const dock = useDatePickerDock(page, step)
  // Held as a local so the picker's callbacks keep the narrowing.
  const dateField = dock.field
  const inlinePicker = DATE_PICKER_IS_INLINE ? dock.Picker : undefined
  const dateDockHeight = inlinePicker === undefined ? dock.height : 0
  const dateFieldId = dateField?.id
  const dateValue = useSelector(kyc.store, (state) =>
    dateFieldId === undefined ? '' : stringValue(state.values[dateFieldId]),
  )

  // One effect and one `onPrefill`, deliberately. The consumer spreads the
  // `values` it closed over at render — `formScreenProps.ts:62` — so two calls
  // in the same commit both build from the same stale base and the second drops
  // what the first wrote. The form store took both writes, so the screen showed
  // those fields filled while the flow submitted without them.
  //
  // Carried values outrank the region guess: an address the buyer typed in an
  // earlier round beats an inference from their IP.
  useEffect(() => {
    if (onPrefill === undefined) {
      return
    }
    const filled: Record<string, string> = {}

    if (carriedValues !== undefined) {
      for (const field of carriedBlankFields(
        form.fields,
        kyc.state.values,
        carriedValues,
      )) {
        filled[field.id] = stringValue(carriedValues[field.id])
      }
    }

    if (defaultCountry !== undefined) {
      const code = defaultCountry.toUpperCase()
      for (const field of blankCountryFields(form.fields, kyc.state.values)) {
        // Never over a carried answer, which is the buyer's own.
        if (filled[field.id] === undefined) {
          filled[field.id] = code
        }
      }
    }

    const entries = Object.entries(filled)
    if (entries.length === 0) {
      return
    }
    // Quietly: the flow gets the one write below, not one per field.
    for (const [fieldId, value] of entries) {
      kyc.setFieldValue(fieldId, value, { dontRunListeners: true })
    }
    onPrefill(filled)
  }, [form, carriedValues, defaultCountry])

  if (page === undefined) {
    return null
  }

  // The round's own error always shows; the page's refusals only once it has
  // been tried, so a blank page is quiet until the user asks to leave it.
  //
  // A picked page is the exception: the wheel always holds a date, so there
  // is nothing to leave blank and its refusal is knowable as it is spun.
  const shownIssues = attempted || dateFieldId !== undefined ? pageIssues : []
  const validationError = formValidationError(errorMessage, shownIssues, t)
  // An age below the floor is not a refusal a press clears: the notice takes
  // the action's place until the wheel moves. Every other refusal keeps its
  // action, because acting is what clears it.
  const isBlocked = shownIssues.includes('DATE_UNDERAGE')
  // Everything standing above the wheel, so its middle can be measured off
  // the frame's: the column's own inset, the heading's, the heading as it
  // actually drew, and the gap under it.
  const wheelTop =
    paddingTop +
    styles.content.paddingTop +
    headingTop(page.section, taxLength) +
    headingHeight +
    KYC_FORM_METRICS.dateHeadingBottom
  // A page tried from the keyboard's return key waits for a clean page to
  // show its action (KYC – Input Complete). Only the last typed field carries
  // the return key that tries it, so it is that field that decides: a number
  // pad has no return key, and a picked date no keyboard at all.
  const typedFields = pageFields.filter(isFocusableField)
  const doneField = typedFields[typedFields.length - 1]
  const canTryFromKeyboard =
    doneField !== undefined &&
    !isDigitsField(doneField) &&
    doneField.id !== dateFieldId
  const shouldShowCta =
    pageIsClean ||
    (taxLength === undefined && !isInlineChoice && !canTryFromKeyboard)

  const back = () => {
    if (step === 0) {
      onClose()
      return
    }
    turn(step - 1)
  }

  // Whichever control is up owns the foot of the screen.
  const dockHeight = dateDockHeight > 0 ? dateDockHeight : keyboardHeight
  // Given up as the picker slides in, not while it is merely available: the
  // page has a date field the whole time, and reserving its slot up front
  // pushed the field into the top half of the screen for no one. The keyboard
  // is down by the time the picker slides in, so the two add rather than trade.
  const dockPadding =
    dateDockHeight > 0
      ? Animated.add(
          keyboard.animated,
          dock.slide.interpolate({
            inputRange: [0, 1],
            outputRange: [0, dateDockHeight],
          }),
        )
      : keyboard.animated
  // Where the action rests over the keyboard: on the home indicator while it
  // is down, and the design's gap above it from the first point it rises.
  const overKeyboard = keyboard.animated.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [
      isSubmitting ? Math.max(0, insets.bottom - 7) : insets.bottom + 2,
      KYC_FORM_METRICS.advanceGap + 1,
      KYC_FORM_METRICS.advanceGap + 2,
    ],
    extrapolateRight: 'extend',
  })

  // The return key walks the step's typed-in fields and then tries it, so the
  // user never reaches for the button between two names.
  const returnKeyFor = (fieldId: string): FieldReturnKey | undefined => {
    const at = typedFields.findIndex((field) => field.id === fieldId)
    if (at < 0) {
      return undefined
    }
    const next = typedFields[at + 1]
    return next === undefined
      ? { kind: 'done', onPress: attempt }
      : { kind: 'next', onPress: () => inputs.current[next.id]?.focus() }
  }

  // One field, drawn the same way loose in a row or as a row of the card.
  const fieldRow = (field: FormField) => (
    <kyc.Field
      key={field.id}
      name={field.id}
      // The client's own rule, layered on connect-core's: a birth date that
      // is well-formed and past can still be too recent.
      validators={kycFieldValidators(
        field,
        field.id === taxField?.id ? taxLength : undefined,
      )}
    >
      {(input) => (
        <View
          style={
            isFixedWidthField(field)
              ? { width: KYC_FORM_METRICS.initialWidth }
              : styles.grow
          }
        >
          <FiatKycFieldControl
            inlineChoice={isInlineChoice}
            ssnDigits={field.id === taxField?.id ? taxLength : undefined}
            Picker={field.id === dateFieldId ? inlinePicker : undefined}
            frame={{ height, top: wheelTop }}
            isDatePickerLoading={dock.isLoading && field.type === 'DATE'}
            caption={fieldCaption(page, field, t)}
            defaultCountry={defaultCountry}
            field={field}
            value={stringValue(input.state.value)}
            // Worn once the page has been tried, so a blank form is quiet.
            issue={attempted ? firstIssue(input.state.meta.errors) : undefined}
            editable={!isSubmitting}
            autoFocus={!isSubmitting && field.id === typedFields[0]?.id}
            grouped={page.grouped}
            picked={
              field.id === dateFieldId
                ? { isOpen: dock.isOpen, onOpen: dock.toggle }
                : undefined
            }
            inputRef={(node) => {
              inputs.current[field.id] = node
            }}
            returnKey={returnKeyFor(field.id)}
            onChangeValue={(value) => {
              if (!isSubmitting) {
                input.handleChange(value)
              }
            }}
            onBlur={input.handleBlur}
          />
        </View>
      )}
    </kyc.Field>
  )

  return (
    <Animated.View
      style={{
        // A flow step's sheet spans the window, status bar included, so the
        // screen takes the whole window and pays the inset itself.
        height,
        paddingTop,
        // Whichever control is up shortens the box — the keyboard, or the
        // picker in its slot. A scroll inset would not: it lets the control be
        // scrolled to, while what is up still covers where it came to rest.
        paddingBottom: dockPadding,
        backgroundColor:
          page.section === 'NAME' ? theme.background.modal : 'transparent',
      }}
    >
      {page.section === 'NAME' ? null : <KycGlassBackdrop />}
      <FiatBackButton onPress={back} />
      <FiatKycPosition
        position={kycFormPosition(
          page.section,
          walkOrder(
            walk ?? walkRound(undefined, form.formId, pages.length),
            step,
          ),
        )}
      />

      {/* A plain scroller, not gorhom's: this screen fills the sheet and
          pays its own insets, so it must render outside one too. */}
      <ScrollView
        style={styles.scroll}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[
          styles.content,
          { paddingTop: headingTop(page.section, taxLength) },
          {
            // Room for the docked control, which floats over this column
            // rather than ending it. The keyboard already covers the home
            // indicator when it is up.
            paddingBottom:
              Math.max(
                dockHeightDrawn,
                advanceDockHeight(validationError !== undefined, isBlocked),
              ) +
              KYC_FORM_METRICS.advanceGap * 2 +
              (dockHeight > 0 ? 0 : insets.bottom),
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* One block per page, keyed so a turn is a leave and an arrival:
            the old page slides its 8px out as the new one slides in. */}
        <Reanimated.View
          key={step}
          entering={pageEntering(turnedForward.current ? 1 : -1)}
          exiting={exitPage}
          style={styles.pageBlock}
        >
          {/* The design keeps one title across the whole form and lets the
            line under it say which details this step wants — left-aligned and
            inset past the fields, with no centred top bar. A page that draws
            its answers carries the section's line alone (2475:8992): the list
            is the page, and a second line above it would crowd the card. */}
          <Box
            onLayout={(event) =>
              setHeadingHeight(event.nativeEvent.layout.height)
            }
            style={[
              styles.heading,
              taxLength === undefined ? undefined : { marginBottom: 13 },
              isAddress ? { marginBottom: 11 } : undefined,
              isInlineChoice
                ? { marginBottom: KYC_FORM_METRICS.questionnaireHeadingBottom }
                : undefined,
              isQuestionnaire && !isInlineChoice
                ? { paddingHorizontal: 16, marginBottom: 16 }
                : undefined,
            ]}
          >
            {isQuestionnaire && !isInlineChoice ? (
              <Text
                style={{
                  fontSize: 22.5,
                  lineHeight: 22.3,
                  fontWeight: '600',
                  letterSpacing: -0.45,
                }}
              >
                {t('fiat.kycTitle')}
              </Text>
            ) : null}
            <Text
              weight="medium"
              style={[
                styles.headingDetail,
                { color: theme.content.elements.secondary },
                isInlineChoice || isDatePage
                  ? {
                      fontSize: KYC_FORM_METRICS.questionnaireTitleFontSize,
                      lineHeight: KYC_FORM_METRICS.questionnaireTitleLineHeight,
                      letterSpacing:
                        KYC_FORM_METRICS.questionnaireTitleTracking,
                      color: theme.content.elements.primary,
                      // The measure is what breaks the line over two, not the
                      // frame's width.
                      ...(isDatePage
                        ? { width: KYC_FORM_METRICS.dateTitleWidth }
                        : {}),
                    }
                  : undefined,
              ]}
            >
              {taxLength === undefined ? (
                sectionDetail(page.section, t)
              ) : (
                <>
                  <KycSecurityIcon color={theme.content.elements.secondary} />{' '}
                  {taxLength === 4
                    ? t('fiat.kycSsnLastFour')
                    : t('fiat.kycDetailSsn')}
                </>
              )}
            </Text>
          </Box>
          {/* A grouped section is one card ruled between its rows; every other
            one is loose boxes with a gap. */}
          {page.grouped ? (
            <Box style={[styles.card, { backgroundColor: theme.form.field }]}>
              {page.rows.map((row, index) => (
                <View key={row[0]?.id}>
                  <View style={styles.row}>{row.map(fieldRow)}</View>
                  {/* Over the row, not above it: the design's rows are 56 apart
                    with the rule on the boundary, so one in flow would push
                    every row below it down by its own width. */}
                  {index === 0 ? null : (
                    <View
                      pointerEvents="none"
                      style={[
                        styles.divider,
                        { backgroundColor: theme.form.divider },
                      ]}
                    />
                  )}
                </View>
              ))}
            </Box>
          ) : (
            page.rows.map((row) => (
              <View key={row[0]?.id} style={styles.row}>
                {row.map(fieldRow)}
              </View>
            ))
          )}
          {taxLength === undefined ? null : (
            <Text
              weight="medium"
              style={{
                fontSize: 12.5,
                letterSpacing: -0.25,
                lineHeight: 14,
                marginLeft: 5,
                marginTop: 10,
                maxWidth: 292,
                color: theme.content.elements.secondary,
              }}
            >
              {t('fiat.kycSsnProtection')}
            </Text>
          )}
        </Reanimated.View>
      </ScrollView>

      {/* What a field losing focus does for the keyboard: a press anywhere off
          the picker puts it away. Under the advance control, which stays live
          so the step can be finished from here. */}
      {dock.isOpen && inlinePicker === undefined ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          onPress={dock.close}
          style={[styles.dismissDatePicker, { bottom: dock.height }]}
        />
      ) : null}

      <FiatFormAdvance
        isLastStep={isLastStep}
        isSubmitting={isSubmitting}
        validationError={validationError}
        isBlocked={isBlocked}
        shouldShowCta={shouldShowCta}
        onHeight={setDockHeightDrawn}
        bottom={
          dateDockHeight > 0
            ? Animated.add(
                overKeyboard,
                dock.slide.interpolate({
                  inputRange: [0, 1],
                  outputRange: [
                    0,
                    dock.height +
                      KYC_FORM_METRICS.datePickerGap -
                      insets.bottom,
                  ],
                }),
              )
            : overKeyboard
        }
        onPress={attempt}
      />

      {inlinePicker !== undefined ||
      dock.Picker === undefined ||
      dateField === undefined ? null : (
        <FiatDatePickerDock
          Picker={dock.Picker}
          value={dateValue}
          isDark={isDarkColor(theme.background.modal)}
          isOpen={dock.isOpen}
          slide={dock.slide}
          // Closed on an answer and on a dismissal alike. Android's dialog
          // closes itself either way, and a picker left mounted afterwards is
          // re-shown by the module on the next render.
          onChangeValue={(value) => {
            kyc.setFieldValue(dateField.id, value)
            dock.close()
          }}
          onDismiss={dock.close}
        />
      )}
    </Animated.View>
  )
}

/**
 * The line above a field where the page's own line does not say enough: the
 * shape a figure is wanted in, or the field's name once two share a page and
 * a typed answer has covered the placeholder that named it.
 */
function fieldCaption(
  page: KycSectionPage,
  field: FormField,
  t: TFunction,
): string | undefined {
  switch (field.id) {
    case GROSS_ANNUAL_INCOME:
      return t('fiat.kycIncomeCaption')
    case EXPECTED_AMOUNT_PER_MONTH:
    case TRANSACTIONS_PER_MONTH:
      return field.labelFallback
  }
  return page.section === 'DETAILS' && field.type === 'COUNTRY'
    ? field.labelFallback
    : undefined
}

function headingTop(
  section: KycSectionPage['section'],
  taxLength?: 4 | 9,
): number {
  if (taxLength === 4) {
    return 28
  }
  if (
    section === 'COUNTRY' ||
    section === 'NATIONALITY' ||
    section === 'ADDRESS'
  ) {
    return 30
  }
  // The list is the page, so its title sits well below the disc rather than
  // right under it (2475:8992).
  if (isInlineChoiceSection(section)) {
    return KYC_FORM_METRICS.questionnaireHeadingTop
  }
  if (section === 'DETAILS') {
    return 32
  }
  return 52
}

const styles = StyleSheet.create({
  dismissDatePicker: { position: 'absolute', top: 0, left: 0, right: 0 },
  scroll: { flex: 1 },
  content: {
    paddingTop: 52,
    paddingHorizontal: KYC_FORM_METRICS.screenInset,
  },
  // The column's own gap moved in here, so the block that slides is the one
  // that lays the rows out.
  pageBlock: { gap: KYC_FORM_METRICS.rowGap },
  heading: {
    // Onto the screen's own inset, so the block clears the fields by 16.
    paddingHorizontal: 6,
    gap: KYC_FORM_METRICS.headingGap,
    marginBottom: 19,
  },
  headingDetail: {
    letterSpacing: -0.42,
    fontSize: KYC_FORM_METRICS.detailFontSize,
    lineHeight: KYC_FORM_METRICS.detailLineHeight,
  },
  row: { flexDirection: 'row', gap: KYC_FORM_METRICS.fieldGap },
  grow: { flex: 1 },
  // The card rounds and clips what it holds, so its rows draw neither.
  card: {
    borderRadius: KYC_FORM_METRICS.fieldRadius,
    overflow: 'hidden',
  },
  divider: {
    position: 'absolute',
    top: 0,
    left: KYC_FORM_METRICS.fieldPaddingHorizontal,
    right: KYC_FORM_METRICS.fieldPaddingHorizontal,
    height: 1,
  },
})
